import { useMemo, useRef, useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Heading from '@zenbusiness/zui/Heading';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import FormStepModal from '#client/components/FormStepModal';
import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import ManualAddInfoBankModal from '#client/components/ManualAddInfoBankModal';
import useModalContext from '#client/hooks/useModalContext';

import { DialogContentStyled, LinkStyled, Wrapper } from './AddCounterPartyModal.styles';
import AddBank from './components/AddBank';
import AddBankAndPayee from './components/AddBankAndPayee';
import AddPayee from './components/AddPayee';
import EditPayee from './components/EditPayee';

import { MODAL_TYPES } from './constants/types';

export default function AddCounterPartyModal({
  handleClose,
  mode = MODAL_TYPES.ADD_BANK_AND_PAYEE,
  refetch,
  entryPageForSendMoney = null,
  expectRedirectTo = '',
  counterPartyInfo,
  deletePayeeCallback,
  actionsKit
}) {
  const theme = useTheme();
  const [isMode, setIsMode] = useState(mode);
  const ref = useRef(null);
  const { openModal } = useModalContext();

  const title = useMemo(() => {
    switch (isMode) {
      case MODAL_TYPES.ADD_BANK_AND_PAYEE:
        return 'Link an account';
      case MODAL_TYPES.ADD_BANK:
        return 'Link a bank account';
      case MODAL_TYPES.ADD_PAYEE:
        return 'Add a new payee';
      case MODAL_TYPES.EDIT_PAYEE:
        return `Edit ${counterPartyInfo.name}`;
      default:
        return '';
    }
  }, [isMode, counterPartyInfo]);

  const modalTitle = useMemo(() => {
    switch (isMode) {
      case MODAL_TYPES.ADD_BANK_AND_PAYEE:
        return 'Link an account';
      case MODAL_TYPES.ADD_BANK:
        return 'Link a bank account';
      case MODAL_TYPES.ADD_PAYEE:
        // We have decided that if a page title repeats the headline below it
        // that we would like to have just one instance of that copy show
        return '';
      case MODAL_TYPES.EDIT_PAYEE:
        return 'Edit Payee';
      default:
        return '';
    }
  }, [isMode]);

  const handleChangeMode = (modeValue) => {
    setIsMode(modeValue);
  };

  const handleCloseAndExitPlaid = () => {
    handleClose();
    ref?.current?.exitPlaid();
  };

  const trackOpenManualInfoAddBankClick = useButtonPressedCallback({
    elementText: 'Can’t find your bank?',
    elementName: 'can’t_find_bank',
    appPageName: 'link_account_modal'
  });

  const handleOpenManualInfoAddBankModal = () => {
    trackOpenManualInfoAddBankClick();
    openModal({
      component: ManualAddInfoBankModal
    });
  };

  return (
    <FormStepModal
      handleClose={handleClose}
      isHideBackButton
      title={modalTitle}
      titleBackground={theme.palette.background.grey.main}
    >
      <DialogContentStyled sx={{ padding: 0 }}>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper
              mb={{ xs: '64px', sm: '120px' }}
              mt={{ xs: '40px', sm: '64px' }}
              role="option"
              spacing={{ xs: '24px', md: '32px' }}
            >
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing="8px"
                textAlign="center"
              >
                <Heading
                  family="secondary"
                  size="4xl"
                  weight="bold"
                >
                  {title}
                </Heading>
                {isMode === MODAL_TYPES.ADD_PAYEE && (
                  <Text size="sm">
                    Link an account. This can’t be your account — link{' '}
                    <Text
                      component="span"
                      size="sm"
                      sx={{
                        whiteSpace: 'nowrap'
                      }}
                    >
                      yours
                      <LinkStyled
                        color="info"
                        onClick={() => handleChangeMode(MODAL_TYPES.ADD_BANK_AND_PAYEE)}
                        sx={{
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {' '}
                        here.
                      </LinkStyled>
                    </Text>
                  </Text>
                )}
              </Stack>
              {isMode === MODAL_TYPES.ADD_BANK && (
                <AddBank
                  actionsKit={actionsKit}
                  handleClose={handleCloseAndExitPlaid}
                  handleOpenManualInfoAddBankModal={handleOpenManualInfoAddBankModal}
                  ref={ref}
                  refetch={refetch}
                />
              )}
              {isMode === MODAL_TYPES.ADD_PAYEE && (
                <AddPayee
                  actionsKit={actionsKit}
                  entryPageForSendMoney={entryPageForSendMoney}
                  expectRedirectTo={expectRedirectTo}
                  handleClose={handleCloseAndExitPlaid}
                  refetch={refetch}
                />
              )}
              {isMode === MODAL_TYPES.ADD_BANK_AND_PAYEE && (
                <AddBankAndPayee
                  actionsKit={actionsKit}
                  entryPageForSendMoney={entryPageForSendMoney}
                  handleChangeMode={handleChangeMode}
                  handleClose={handleCloseAndExitPlaid}
                  handleOpenManualInfoAddBankModal={handleOpenManualInfoAddBankModal}
                  ref={ref}
                  refetch={refetch}
                />
              )}
              {isMode === MODAL_TYPES.EDIT_PAYEE && (
                <EditPayee
                  counterPartyInfo={counterPartyInfo}
                  deletePayeeCallback={deletePayeeCallback ?? refetch}
                  editPayeeCallback={refetch}
                  handleClose={handleCloseAndExitPlaid}
                />
              )}
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
    </FormStepModal>
  );
}

AddCounterPartyModal.MODAL_TYPES = MODAL_TYPES;

AddCounterPartyModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  mode: PropTypes.string,
  refetch: PropTypes.func,
  deletePayeeCallback: PropTypes.func,
  entryPageForSendMoney: PropTypes.string,
  expectRedirectTo: PropTypes.string,
  actionsKit: PropTypes.shape({
    resetSearchParameters: PropTypes.func
  }),
  counterPartyInfo: PropTypes.shape({
    name: PropTypes.string,
    bank: PropTypes.string,
    connectedType: PropTypes.string,
    accountNumber: PropTypes.string,
    accountType: PropTypes.string,
    fullAccountNumber: PropTypes.string,
    fullRoutingNumber: PropTypes.string,
    wire: PropTypes.shape({
      address: PropTypes.shape({
        city: PropTypes.string,
        country: PropTypes.string,
        postalCode: PropTypes.string,
        state: PropTypes.string,
        street: PropTypes.string,
        street2: PropTypes.string
      }),
      routingNumber: PropTypes.string
    }),
    type: PropTypes.string,
    routingNumber: PropTypes.string,
    isWireSupported: PropTypes.bool,
    isACHSupported: PropTypes.bool
  })
};
